/**
 * /settings/webhooks -- tenant webhook subscriptions UI.
 *
 * Admins can:
 *   - Create a subscription (URL + events) -- the secret is shown ONCE.
 *   - List existing subscriptions with status (active / auto-deactivated).
 *   - Send a webhook.test ping.
 *   - Re-enable an auto-deactivated subscription.
 *   - Rotate the signing secret.
 *   - Delete a subscription.
 *   - View the last 100 deliveries (status code, attempt, duration, error).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Webhook,
  Copy,
  Check,
  PlayCircle,
  RotateCw,
  Trash2,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  BookOpen,
} from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface Subscription {
  id: string;
  name: string;
  description?: string | null;
  url: string;
  events: string[];
  isActive: boolean;
  consecutiveFailures: number;
  lastDeliveryAt?: string | null;
  lastDeliveryStatus?: number | null;
  autoDeactivatedAt?: string | null;
  autoDeactivatedReason?: string | null;
  createdAt: string;
  secretPrefix: string;
}

interface CreatedSubscription extends Subscription {
  secret: string;
}

interface Delivery {
  id: string;
  event: string;
  attempt: number;
  success: boolean;
  statusCode?: number | null;
  responseBody?: string | null;
  errorMessage?: string | null;
  durationMs?: number | null;
  deliveredAt: string;
}

export function SettingsWebhooksPage() {
  const qc = useQueryClient();
  const subs = useQuery({
    queryKey: ['webhooks'],
    queryFn: async () => (await api.get<{ items: Subscription[] }>('/api/webhooks')).data.items,
  });
  const events = useQuery({
    queryKey: ['webhook-events'],
    queryFn: async () => (await api.get<{ events: string[] }>('/api/webhooks/events')).data.events,
  });

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [revealedSecret, setRevealedSecret] = useState<{ id: string; secret: string } | null>(null);

  useEffect(() => {
    if (events.data && picked.size === 0) {
      // Default-pick the most useful events for new pilots.
      setPicked(new Set(events.data.filter((e) => e === 'workflow.run.completed' || e === 'extraction.review.completed')));
    }
  }, [events.data, picked.size]);

  const create = useMutation({
    mutationFn: async () => {
      const res = await api.post<CreatedSubscription>('/api/webhooks', {
        name,
        description: description || undefined,
        url,
        events: Array.from(picked),
      });
      return res.data;
    },
    onSuccess: (created) => {
      toast.success('Webhook created', 'Copy the secret now -- it will not be shown again.');
      setRevealedSecret({ id: created.id, secret: created.secret });
      setName('');
      setUrl('');
      setDescription('');
      qc.invalidateQueries({ queryKey: ['webhooks'] });
    },
    onError: (err) => toast.error('Create failed', extractApiError(err).message),
  });

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="font-display text-2xl">Webhooks</h1>
        <p className="text-sm text-muted-foreground">
          Push Praxara events to your endpoints (LIMS, QMS, Slack, Power Automate). Every request is signed with HMAC-SHA-256 using a per-subscription secret.
        </p>
        <div>
          <Link to="/docs/webhooks" className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
            <BookOpen className="h-3.5 w-3.5" /> Receiver verification guide (Node + Python)
          </Link>
        </div>
      </header>

      {revealedSecret && (
        <SecretRevealCard
          secret={revealedSecret.secret}
          onDismiss={() => setRevealedSecret(null)}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle><span className="inline-flex items-center gap-2"><Webhook className="h-5 w-5" /> New subscription</span></CardTitle>
          <CardDescription>HTTPS only in production. The secret is shown once when you save.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label>Name</Label>
              <Input placeholder="e.g. LIMS production" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label>URL</Label>
              <Input placeholder="https://your-system.example.com/praxara" value={url} onChange={(e) => setUrl(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Input placeholder="What is this hook for?" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div>
            <Label className="mb-1.5 block">Events</Label>
            {events.data ? (
              <div className="grid gap-1.5 md:grid-cols-2">
                {events.data.map((ev) => (
                  <label key={ev} className="flex cursor-pointer items-start gap-2 rounded border p-2 text-sm hover:bg-accent/40">
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-primary"
                      checked={picked.has(ev)}
                      onChange={(e) => {
                        const next = new Set(picked);
                        if (e.target.checked) next.add(ev); else next.delete(ev);
                        setPicked(next);
                      }}
                    />
                    <code className="text-xs">{ev}</code>
                  </label>
                ))}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">Loading...</div>
            )}
          </div>
          <div>
            <Button
              onClick={() => create.mutate()}
              disabled={create.isPending || !name || !url || picked.size === 0}
            >
              {create.isPending ? 'Saving...' : 'Create webhook'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Active subscriptions</CardTitle>
          <CardDescription>{subs.data?.length ?? 0} configured.</CardDescription>
        </CardHeader>
        <CardContent>
          {subs.isLoading && <div className="text-sm text-muted-foreground">Loading...</div>}
          {subs.data && subs.data.length === 0 && (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              No webhook subscriptions yet. Create one above to start receiving events.
            </div>
          )}
          <div className="space-y-3">
            {subs.data?.map((s) => (
              <SubscriptionRow key={s.id} sub={s} onChanged={() => qc.invalidateQueries({ queryKey: ['webhooks'] })} />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SecretRevealCard({ secret, onDismiss }: { secret: string; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };
  return (
    <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
      <CardContent className="space-y-2 p-4">
        <div className="flex items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
          <AlertTriangle className="h-4 w-4" /> Copy the signing secret -- it will not be shown again.
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 truncate rounded border bg-background px-2 py-1.5 font-mono text-xs">{secret}</code>
          <Button size="sm" variant="outline" onClick={copy}>
            {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />} {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button size="sm" variant="ghost" onClick={onDismiss}>Dismiss</Button>
        </div>
        <div className="text-[11px] text-muted-foreground">
          Verify deliveries with <code>HMAC-SHA-256(timestamp + "." + body)</code>. Headers: <code>X-Praxara-Timestamp</code>, <code>X-Praxara-Signature</code>.
        </div>
      </CardContent>
    </Card>
  );
}

function SubscriptionRow({ sub, onChanged }: { sub: Subscription; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const test = useMutation({
    mutationFn: async () => api.post(`/api/webhooks/${sub.id}/test`),
    onSuccess: () => toast.success('Test delivery queued', 'Check your endpoint logs and the delivery list.'),
    onError: (err) => toast.error('Test failed', extractApiError(err).message),
  });
  const enable = useMutation({
    mutationFn: async () => api.patch(`/api/webhooks/${sub.id}`, { isActive: true }),
    onSuccess: () => { toast.success('Re-enabled'); onChanged(); },
    onError: (err) => toast.error('Update failed', extractApiError(err).message),
  });
  const remove = useMutation({
    mutationFn: async () => api.delete(`/api/webhooks/${sub.id}`),
    onSuccess: () => { toast.success('Deleted'); onChanged(); },
    onError: (err) => toast.error('Delete failed', extractApiError(err).message),
  });
  const rotate = useMutation({
    mutationFn: async () => (await api.post<{ secret: string }>(`/api/webhooks/${sub.id}/rotate-secret`)).data,
    onSuccess: (data) => {
      toast.success('Secret rotated', 'Copy now -- not shown again.');
      // Quick-and-dirty reveal -- prompt the user to copy.
      window.prompt('New signing secret (copy now -- not shown again):', data.secret);
      onChanged();
    },
    onError: (err) => toast.error('Rotate failed', extractApiError(err).message),
  });

  const status = sub.isActive
    ? { tone: 'success' as const, label: 'Active' }
    : sub.autoDeactivatedAt
      ? { tone: 'destructive' as const, label: 'Auto-disabled' }
      : { tone: 'secondary' as const, label: 'Disabled' };

  return (
    <div className="rounded-md border">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start gap-3 p-3 text-left hover:bg-accent/40"
      >
        {open ? <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-medium">{sub.name}</span>
            <Badge variant={status.tone}>{status.label}</Badge>
            {sub.consecutiveFailures > 0 && (
              <Badge variant="destructive" className="text-[10px]">{sub.consecutiveFailures} consecutive failures</Badge>
            )}
          </div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">{sub.url}</div>
          <div className="mt-1 flex flex-wrap gap-1">
            {sub.events.slice(0, 6).map((e) => (
              <code key={e} className="rounded border bg-muted/40 px-1.5 py-0.5 text-[10px]">{e}</code>
            ))}
            {sub.events.length > 6 && (
              <span className="text-[10px] text-muted-foreground">+{sub.events.length - 6} more</span>
            )}
          </div>
        </div>
      </button>
      {open && (
        <div className="space-y-3 border-t p-3 text-sm">
          {sub.autoDeactivatedAt && sub.autoDeactivatedReason && (
            <div className="rounded border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
              {sub.autoDeactivatedReason}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => test.mutate()} disabled={test.isPending || !sub.isActive}>
              <PlayCircle className="mr-1 h-3.5 w-3.5" /> {test.isPending ? 'Sending...' : 'Send test'}
            </Button>
            {!sub.isActive && (
              <Button size="sm" variant="outline" onClick={() => enable.mutate()}>
                Re-enable
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => rotate.mutate()}>
              <RotateCw className="mr-1 h-3.5 w-3.5" /> Rotate secret
            </Button>
            <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => {
              if (window.confirm(`Delete webhook "${sub.name}"?`)) remove.mutate();
            }}>
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
            </Button>
          </div>
          <DeliveriesPanel id={sub.id} />
        </div>
      )}
    </div>
  );
}

function DeliveriesPanel({ id }: { id: string }) {
  const deliveries = useQuery({
    queryKey: ['webhooks', id, 'deliveries'],
    queryFn: async () => (await api.get<{ items: Delivery[] }>(`/api/webhooks/${id}/deliveries`)).data.items,
    refetchInterval: 5000,
  });
  const items = deliveries.data ?? [];
  const summary = useMemo(() => {
    const ok = items.filter((d) => d.success).length;
    return `${ok}/${items.length} successful`;
  }, [items]);

  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
        <span>Recent deliveries (last 100)</span>
        <span>{summary}</span>
      </div>
      <div className="overflow-x-auto rounded border">
        <table className="w-full text-xs">
          <thead className="bg-muted/30 text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 text-left">When</th>
              <th className="px-2 py-1.5 text-left">Event</th>
              <th className="px-2 py-1.5 text-left">Attempt</th>
              <th className="px-2 py-1.5 text-left">Status</th>
              <th className="px-2 py-1.5 text-left">Duration</th>
              <th className="px-2 py-1.5 text-left">Error</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-2 py-3 text-center text-muted-foreground">No deliveries yet.</td>
              </tr>
            )}
            {items.map((d) => (
              <tr key={d.id} className="border-t">
                <td className="px-2 py-1.5 whitespace-nowrap">{new Date(d.deliveredAt).toLocaleString()}</td>
                <td className="px-2 py-1.5"><code className="text-[10px]">{d.event}</code></td>
                <td className="px-2 py-1.5">{d.attempt}</td>
                <td className={cn('px-2 py-1.5', d.success ? 'text-emerald-600' : 'text-destructive')}>{d.statusCode ?? '-'}</td>
                <td className="px-2 py-1.5">{d.durationMs ? `${d.durationMs} ms` : '-'}</td>
                <td className="px-2 py-1.5 truncate text-muted-foreground" title={d.errorMessage ?? ''}>{d.errorMessage ?? d.responseBody?.slice(0, 60) ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
